// Foreground picker for theme-invariant surfaces.
//
// Stimulus colours (stroop's ink table, speedMatch's cards, the tower's balls)
// are literal hexes that do NOT change with the theme, so anything drawn ON one
// of them cannot be derived from a theme token either: `--text-inverted`
// resolves to #ffffff in light mode, which puts white text on a yellow swatch
// at ~1.4:1. Derive the foreground from the swatch's own luminance instead.

// Near-black rather than pure #000 so the label matches the app's ink weight;
// near-white rather than #fff for the same reason on dark swatches.
export const ON_LIGHT = '#14141A';
export const ON_DARK = '#FFFFFF';

const channel = (v) => {
  const x = v / 255;
  return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
};

// WCAG relative luminance. Returns null for anything that is not a 3- or
// 6-digit hex — callers must fall back to theme tokens rather than guess.
export function relativeLuminance(hex) {
  if (typeof hex !== 'string') return null;
  let c = hex.trim().replace(/^#/, '');
  if (c.length === 3) c = c.split('').map((ch) => ch + ch).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(c)) return null;
  const [r, g, b] = [0, 2, 4].map((i) => channel(parseInt(c.slice(i, i + 2), 16)));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(hexA, hexB) {
  const a = relativeLuminance(hexA);
  const b = relativeLuminance(hexB);
  if (a === null || b === null) return null;
  const [hi, lo] = a > b ? [a, b] : [b, a];
  return (hi + 0.05) / (lo + 0.05);
}

// The higher-contrast of near-black / near-white against `hex`, or null when
// `hex` is not a parseable colour.
export function readableOn(hex) {
  const l = relativeLuminance(hex);
  if (l === null) return null;
  const onLight = (l + 0.05) / (relativeLuminance(ON_LIGHT) + 0.05);
  const onDark = (relativeLuminance(ON_DARK) + 0.05) / (l + 0.05);
  return onLight >= onDark ? ON_LIGHT : ON_DARK;
}
